/*
 * EditModeScreen
 *
 * Edit mode screen component with action buttons for mapping admin
 */

import { useSelector } from 'react-redux';
import { useState } from 'react';
import { SCREEN_MODES, TABS } from '../constants';
import MappingGridContainer from './MappingGridContainer';
import {
  Row,
  Col,
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import {
  addEmptyRow,
  cancelDraftRow,
  clearEditModeSnapshot,
  clearRowChanges,
  loadMappingData,
  clearLoadedTabs,
  setScreenMode,
  saveMappingData,
  setUpdatedOnlyFilter,
  setRowEditValidationModal,
} from '../../../features/mappingAdmin/mappingAdminSlice';
import { MappingAdminState } from '../../../common/domain/mappingAdmin';
import { EditModeScreenProps } from '../interfaces';
import ConfirmationModal from '../../../common/ConfirmationModal';

const EditModeTabContent = ({ username, dispatch }: EditModeScreenProps) => {
  const {
    addedRows,
    editedRows,
    deletedRows,
    activeTab,
    draftRow,
    rowEditInProgress,
    loading,
    saving,
    showUpdatedOnly,
    rowEditValidationModal,
  } = useSelector((state: any) => state.mappingAdmin as MappingAdminState);

  const changesPresent =
    Object.keys(addedRows).length > 0 ||
    Object.keys(editedRows).length > 0 ||
    Object.keys(deletedRows).length > 0 ||
    draftRow !== null;

  const [confirmation, setConfirmation] = useState<{
    isOpen: boolean;
    title: string;
    message: React.ReactNode;
    onConfirm: () => void;
  } | null>(null);

  const switchToViewingMode = () => {
    dispatch(clearEditModeSnapshot());
    dispatch(clearRowChanges({ tabType: TABS.RATES_FIELD }));
    dispatch(clearRowChanges({ tabType: TABS.RATES_FIXING }));
    dispatch(clearLoadedTabs());
    dispatch(loadMappingData(activeTab));
    dispatch(setScreenMode(SCREEN_MODES.VIEWING_MODE));
    dispatch(setUpdatedOnlyFilter(false));
  };

  const onConfirmSave = () => {
    setConfirmation({
      isOpen: true,
      title: 'Save Changes',
      message: (
        <>
          You are about to save changes:
          <ul>
            <li>Records added: {Object.keys(addedRows).length}</li>
            <li>Records updated: {Object.keys(editedRows).length}</li>
            <li>Records deleted: {Object.keys(deletedRows).length}</li>
          </ul>
          Continue?
        </>
      ),
      onConfirm: onSave,
    });
  };

  const onSave = async () => {
    await dispatch(saveMappingData(activeTab));
    setConfirmation(null);
    switchToViewingMode();
  };

  const onCancel = () => {
    setConfirmation(null);
  };

  const onConfirmBack = () => {
    setConfirmation({
      isOpen: true,
      title: 'Leaving editing mode',
      message:
        'You are about to switch to viewing mode. All unsaved changes will be lost. Continue?',
      onConfirm: onBack,
    });
  };
  const onBack = () => {
    dispatch(cancelDraftRow());
    switchToViewingMode();
  };

  const onConfirmReset = () => {
    setConfirmation({
      isOpen: true,
      title: 'Resetting changes',
      message:
        'You are about to reset changes. All unsaved changes will be lost. Continue?',
      onConfirm: onResetChanges,
    });
  };

  const onResetChanges = () => {
    dispatch(cancelDraftRow());
    dispatch(clearRowChanges({ tabType: activeTab }));
    setConfirmation(null);
    dispatch(setUpdatedOnlyFilter(false));
  };

  const onAddRow = () => {
    dispatch(addEmptyRow({ tabType: activeTab, username }));
  };

  const onToggleShowUpdatedOnly = () => {
    dispatch(setUpdatedOnlyFilter(!showUpdatedOnly));
  };

  return (
    <>
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <Button color="secondary" size="sm" onClick={onConfirmBack}>
              Back to Viewing Mode
            </Button>
            <Button
              color="primary"
              size="sm"
              onClick={onAddRow}
              disabled={rowEditInProgress || loading}
            >
              +
            </Button>
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <MappingGridContainer username={username} dispatch={dispatch} />
        </Col>
      </Row>
      <Row>
        <Col>
          <FormGroup className="d-flex justify-content-end align-items-center mt-3">
            <FormGroup
              check
              className="mr-4 d-flex form-check align-items-center mb-0"
            >
              <Label check>
                <Input
                  check
                  type="checkbox"
                  id="show-updated-only"
                  checked={showUpdatedOnly}
                  onChange={onToggleShowUpdatedOnly}
                />
                Show edited rows only
                <span className="form-check-sign"></span>
              </Label>
            </FormGroup>
            <Button
              color="secondary"
              size="sm"
              className="mr-2"
              disabled={!changesPresent || rowEditInProgress}
              onClick={onConfirmReset}
            >
              Reset changes
            </Button>
            <Button
              color="primary"
              size="sm"
              onClick={onConfirmSave}
              disabled={!changesPresent || rowEditInProgress}
            >
              Save changes
            </Button>
          </FormGroup>
        </Col>
      </Row>

      <ConfirmationModal
        isOpen={confirmation?.isOpen || false}
        title={confirmation?.title || ''}
        message={confirmation?.message || ''}
        confirmColor="primary"
        loading={saving}
        onConfirm={confirmation?.onConfirm || (() => {})}
        onCancel={onCancel}
      />

      <Modal
        isOpen={rowEditValidationModal.isOpen}
        size="md"
        toggle={() =>
          dispatch(setRowEditValidationModal({ isOpen: false, message: '' }))
        }
      >
        <ModalHeader>Validation Error</ModalHeader>
        <ModalBody>{rowEditValidationModal.message}</ModalBody>
        <ModalFooter style={{ padding: '1em' }}>
          <Button
            color="primary"
            onClick={() =>
              dispatch(
                setRowEditValidationModal({ isOpen: false, message: '' }),
              )
            }
          >
            OK
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default EditModeTabContent;
